import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, type StrategicKpis, type TrendPoint, type TaskRow } from "../api.ts";
import { KpiCard } from "../components/KpiCard.tsx";
import { TrendChart, DonutChart, HorizontalBar } from "../components/charts/index.ts";
import { fmtBRL, fmtBRLFull, fmtNum, fmtDate } from "../lib/format.ts";

interface Financial {
  summary: {
    received_this_month: number;
    received_prev_month: number;
    open_count: number;
    open_amount: number;
    overdue_count: number;
    overdue_amount: number;
    avg_ticket_this_month: number;
  };
  monthly_series: Array<{ month: string; revenue: number; transactions: number }>;
  top_categories_30d: Array<{ name: string | null; code: string | null; revenue: number; transactions: number }>;
  catalog: { total: number; with_family: number };
}

interface Channel { channel: string; sessions: number; pageviews: number; days: number }
interface Product { name: string; revenue: number; units: number; orders: number }
interface SparkRow { date: string; revenue: number; orders: number; organic_sessions: number; sessions: number }

const CHANNEL_LABEL: Record<string, string> = {
  organic:  "Orgânico (Search)",
  campaign: "Campanhas (Ads)",
  direct:   "Direto",
  social:   "Social",
  referral: "Sites referidos",
  ai:       "AI Assistants",
  all:      "Tráfego histórico",
};

const CHANNEL_COLOR: Record<string, string> = {
  organic:  "#047857",
  campaign: "#7c3aed",
  direct:   "#0891b2",
  social:   "#b91c1c",
  referral: "#b45309",
  ai:       "#ffd000",
  all:      "#9aa39d",
};

export function StrategicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [kpis, setKpis] = useState<StrategicKpis | null>(null);
  const [trend, setTrend] = useState<TrendPoint[] | null>(null);
  const [markers, setMarkers] = useState<Array<{ date: string; title: string }>>([]);
  const [upcoming, setUpcoming] = useState<TaskRow[] | null>(null);
  const [fin, setFin] = useState<Financial | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sparks, setSparks] = useState<SparkRow[]>([]);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    setErr(null);
    Promise.all([
      api.get<StrategicKpis>(`/api/t/${slug}/kpis/strategic`),
      api.get<{ series: TrendPoint[] }>(`/api/t/${slug}/kpis/trend?days=90`),
      api.get<{ markers: Array<{ date: string; title: string }> }>(`/api/t/${slug}/tasks/markers?days=90`),
      api.get<{ tasks: TaskRow[] }>(`/api/t/${slug}/tasks?status=pending`),
      api.get<Financial>(`/api/t/${slug}/kpis/financial`),
      api.get<{ channels: Channel[] }>(`/api/t/${slug}/kpis/channels?days=90`),
      api.get<{ products: Product[] }>(`/api/t/${slug}/kpis/top-products?days=30`),
      api.get<{ series: SparkRow[] }>(`/api/t/${slug}/kpis/sparklines`),
    ])
      .then(([k, t, m, u, f, c, p, s]) => {
        setKpis(k);
        setTrend(t.series);
        setMarkers(m.markers);
        setUpcoming(u.tasks.slice(0, 6));
        setFin(f);
        setChannels(c.channels);
        setProducts(p.products);
        setSparks(s.series);
      })
      .catch((e) => setErr(e.message));
  }, [slug]);

  if (err) return <div className="center-loading">erro: {err}</div>;
  if (!kpis || !trend) return <div className="center-loading">carregando…</div>;

  const usingReceivables = kpis.orders.current === 0 && kpis.orders.prev === 0;

  const sparkRev  = sparks.map(r => r.revenue);
  const sparkOrd  = sparks.map(r => r.orders);
  const sparkOrg  = sparks.map(r => r.organic_sessions);

  const channelSlices = channels
    .filter((c) => c.channel !== "all" && c.sessions > 0)
    .map((c) => ({
      name: CHANNEL_LABEL[c.channel] ?? c.channel,
      value: c.sessions,
      color: CHANNEL_COLOR[c.channel],
    }));
  const totalSessions = channelSlices.reduce((s, c) => s + c.value, 0);

  const topCatsData = (fin?.top_categories_30d ?? [])
    .filter(c => c.revenue > 0)
    .slice(0, 10)
    .map((c) => ({ name: (c.name ?? c.code ?? "(sem categoria)").slice(0, 38), value: c.revenue }));

  const topProductsData = products
    .slice(0, 10)
    .map((p) => ({ name: p.name.slice(0, 50), value: p.revenue }));

  return (
    <>
      <div className="section-head">
        <div>
          <p className="eyebrow">
            Mês atual {usingReceivables && <span style={{ opacity: .65 }}>· receita via OMIE (orders iSET ainda vazio)</span>}
          </p>
          <h1>Resumo executivo</h1>
        </div>
      </div>

      <section className="section">
        <div className="kpi-grid">
          <KpiCard label="Receita do mês"     data={kpis.monthly_revenue} format={fmtBRL}     sparkline={sparkRev} />
          <KpiCard label="Tráfego orgânico"   data={kpis.organic_traffic} format={fmtNum} unit=" sessões" sparkline={sparkOrg} />
          <KpiCard label="Pedidos"            data={kpis.orders}          format={fmtNum}    sparkline={sparkOrd} />
          <KpiCard label="Ticket médio"       data={kpis.avg_order_value} format={fmtBRLFull} />
          <KpiCard label="ROAS (últimos 30d)" data={kpis.roas}            format={(n) => n.toFixed(2)} unit="×" />
          <KpiCard label="Conversão"          data={kpis.conversion_rate} format={(n) => `${n.toFixed(2)}%`} />
        </div>
      </section>

      <section className="section">
        <div className="section-head">
          <div>
            <p className="eyebrow">Tendência · 90 dias</p>
            <h2>Receita × tráfego orgânico</h2>
          </div>
        </div>
        <div className="card">
          <TrendChart data={trend} markers={markers} />
        </div>
      </section>

      {channelSlices.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Aquisição · últimos 90 dias</p>
              <h2>De onde vem o tráfego</h2>
            </div>
          </div>
          <div className="grid g2">
            <div className="card">
              <h3>Composição por canal</h3>
              <DonutChart
                data={channelSlices}
                centerLabel={{ value: fmtNum(totalSessions), sub: "sessões 90d" }}
                height={300}
              />
            </div>
            <div className="card">
              <h3>Sessões por canal</h3>
              <table className="simple">
                <thead><tr><th>Canal</th><th style={{ textAlign: "right" }}>Sessões</th><th style={{ textAlign: "right" }}>%</th></tr></thead>
                <tbody>
                  {channelSlices.map((c, i) => (
                    <tr key={i}>
                      <td>
                        <span style={{
                          display: "inline-block", width: 10, height: 10, borderRadius: 2,
                          background: c.color, marginRight: ".5rem", verticalAlign: "middle",
                        }} />
                        <strong>{c.name}</strong>
                      </td>
                      <td style={{ textAlign: "right" }} className="mono">{fmtNum(c.value)}</td>
                      <td style={{ textAlign: "right" }} className="mono">{((c.value / totalSessions) * 100).toFixed(1)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {fin && (
        <section className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Financeiro · OMIE</p>
              <h2>Recebíveis e catálogo</h2>
            </div>
          </div>
          <div className="kpi-grid">
            <div className="kpi-card">
              <div className="label">Em aberto (próx. vencimento)</div>
              <div className="value">{fmtBRL(fin.summary.open_amount)}</div>
              <div className="meta"><span className="mono">{fin.summary.open_count} títulos</span></div>
            </div>
            <div className="kpi-card" style={{
              borderColor: fin.summary.overdue_count > 0 ? "var(--red)" : "var(--border)",
              boxShadow: fin.summary.overdue_count > 0 ? "0 0 0 3px rgba(185,28,28,.06)" : "var(--shadow-sm)",
            }}>
              <div className="label" style={{ color: fin.summary.overdue_count > 0 ? "var(--red)" : "var(--text-muted)" }}>
                Vencidos ⚠
              </div>
              <div className="value">{fmtBRL(fin.summary.overdue_amount)}</div>
              <div className="meta"><span className="mono">{fin.summary.overdue_count} títulos vencidos</span></div>
            </div>
            <div className="kpi-card">
              <div className="label">Catálogo OMIE</div>
              <div className="value">{fmtNum(fin.catalog.total)}<small>itens</small></div>
              <div className="meta">
                <span className="mono">{fin.catalog.with_family} com família</span>
                {fin.catalog.total > 0 && (
                  <span>· {Math.round(fin.catalog.with_family / fin.catalog.total * 100)}% organizados</span>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {topCatsData.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Últimos 30 dias</p>
              <h2>Top categorias por receita</h2>
            </div>
          </div>
          <div className="card">
            <HorizontalBar data={topCatsData} height={Math.max(220, topCatsData.length * 32)} />
          </div>
        </section>
      )}

      {topProductsData.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Últimos 30 dias</p>
              <h2>Top produtos vendidos (iSET)</h2>
            </div>
          </div>
          <div className="card">
            <HorizontalBar data={topProductsData} height={Math.max(220, topProductsData.length * 32)} />
          </div>
        </section>
      )}

      {upcoming && upcoming.length > 0 && (
        <section className="section">
          <div className="section-head">
            <div>
              <p className="eyebrow">Plano · próximos passos</p>
              <h2>Checkpoints</h2>
            </div>
          </div>
          <div className="card">
            {upcoming.map((t) => (
              <div key={t.id} className="list-row">
                <span>
                  <strong style={{ marginRight: ".5rem" }}>{t.title}</strong>
                  {t.front && <span className="pill" style={{ marginRight: ".4rem" }}>{t.front}</span>}
                  {t.owner && <span className="mono" style={{ fontSize: ".78rem", opacity: .55 }}>{t.owner}</span>}
                </span>
                <span className="mono" style={{ fontSize: ".82rem" }}>{fmtDate(t.due_date)}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
